import { useObserver } from 'mobx-react';
import { useRouter } from 'next/compat/router';

import { Product } from 'types/graphql-overrides';
import type { Maybe, CollectionCardBadge } from 'src/gql/graphql';
import useCart from 'src/hooks/use-cart';
import usePaths from 'src/hooks/use-paths';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import { useShowTotalTerpenes } from 'shared/hooks/use-show-total-terpenes';
import { getPotencyStrings } from 'src/utils/helpers/product';
import { productIsPartOfActiveSpecial, isNotSolitaryMinimumSpendOffer } from 'shared/helpers/specials';
import { useProductPricing } from 'src/hooks/use-product-pricing';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import type { ProductCardBadgeSpecialData, ProductCardSpecialKind } from './types';

type UseCardProps = {
  product: Product;
};

type UseCardReturn = {
  href: string;
  isStaffPick: boolean;
  isSponsored: boolean;
  brandName: string;
  strainType: Maybe<string> | undefined;
  potency: string[];
  isSpecialOffer: boolean;
  specialNames: string[];
  specials: ProductCardBadgeSpecialData[];
  hideDiscount: boolean;
  hideSalesLanguage: boolean;
  priceOptions: ReturnType<ReturnType<typeof useProductPricing>['getPriceOptions']>;
  collectionCardBadge: CollectionCardBadge | null;
};

const specialMatchesActiveMenuType = (specialMenuType?: string | null, activeMenuType?: string | null): boolean => {
  if (!specialMenuType || specialMenuType === 'both') return true;
  const isMed = activeMenuType === 'med' || activeMenuType === 'medical';
  const isRec = activeMenuType === 'rec' || activeMenuType === 'recreational';
  if (specialMenuType === 'medical') return isMed;
  if (specialMenuType === 'recreational') return isRec;
  return true;
};

const mapSpecialKind = (kind?: string | null): ProductCardSpecialKind => {
  switch (kind) {
    case 'percent_off':
      return 'percent-off';
    case 'dollar_off':
      return 'dollar-off';
    case 'mix_and_match':
      return 'mix-and-match';
    case 'bogo':
      return 'bogo';
    case null:
    case undefined:
    default:
      return 'unknown';
  }
};

export const useCard = ({ product }: UseCardProps): UseCardReturn => {
  const { href } = usePaths({
    product,
  });

  const isStaffPick = !!product.featured?.current;
  const isSponsored = !!product.adTrackers;
  const brandName = product.brand.name;
  const strainType = product.strainType !== 'N/A' ? product.strainType : null;

  const { dispensary } = useDispensary();
  const showTotalTerpenes = useShowTotalTerpenes(dispensary);

  const productForPotencyHelpers = product.thcContent
    ? { ...product, THCContent: product.thcContent, CBDContent: product.cbdContent }
    : product;
  const { THC: THCString, CBD: CBDString, TAC: TACString, TERPS: TERPSString } = getPotencyStrings(
    productForPotencyHelpers
  );
  const showTAC = !!product.cannabinoidsV2?.length;
  const potency: string[] = [];

  if (showTAC && typeof TACString === 'string') {
    potency.push(`TAC: ${TACString}`);
  }

  if (typeof THCString === 'string') {
    potency.push(`THC: ${THCString}`);
  }

  if (typeof CBDString === 'string') {
    potency.push(`CBD: ${CBDString}`);
  }

  if (showTotalTerpenes && typeof TERPSString === 'string') {
    potency.push(`TERPS: ${TERPSString}`);
  }

  const { query: routerQuery = {} } = useRouter() ?? {};
  const Cart = useCart();
  const menuType = useObserver(() => Cart.menuType);
  const hideDiscount = useHideDiscountFromConsumer(dispensary);
  const hideSalesLanguage = useHideSalesLanguage();
  const isInBogo = productIsPartOfActiveSpecial(product, menuType);
  // posSyncedPricingApplied is stamped server-side by applyPOSPrices and is the source of truth for whether
  // POS-synced pricing (and therefore POS-synced discounts) own this product's offers on the active menu.
  // When true, legacy bogoSpecials are ignored entirely -- even if there are no POS discounts to show.
  // posDiscountNames is already scoped to the menus whose POS prices were applied.
  const hasPosSyncedPricing = Boolean(
    menuType === 'med' ? product.posSyncedPricingApplied?.medical : product.posSyncedPricingApplied?.recreational
  );
  const posDiscountNames = (product.posDiscountNames ?? []).filter((name): name is string => Boolean(name));
  const isSpecialOffer =
    !routerQuery.specialId &&
    !hideDiscount &&
    (hasPosSyncedPricing
      ? posDiscountNames.length > 0
      : isInBogo && isNotSolitaryMinimumSpendOffer(product.specialData?.bogoSpecials));

  const specialNames = hasPosSyncedPricing
    ? posDiscountNames
    : (product.specialData?.bogoSpecials ?? [])
        .filter((special) => special?.qualifyingOptions && special.qualifyingOptions.length > 0)
        .map((special) => special?.menuDisplayName ?? special?.specialName)
        .filter((name): name is string => Boolean(name));

  const isSpecialRoute = Boolean(routerQuery.specialId);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const shouldSuppressSpecials = isSpecialRoute || hideDiscount || hideSalesLanguage;

  const isSpecialKindEligible = (kind: string | null | undefined): boolean => {
    // Offers (BOGO, mix-and-match) require a qualifying active offer (isSpecialOffer === true).
    // Solitary spend-threshold offers have isSpecialOffer === false and are suppressed.
    if (kind === 'bogo' || kind === 'mix_and_match') {
      return isSpecialOffer;
    }
    // Direct sales (percent_off, dollar_off, unknown) are eligible unless globally suppressed
    return true;
  };

  const specials: ProductCardBadgeSpecialData[] = shouldSuppressSpecials
    ? []
    : (product.productCardSpecials ?? [])
        .filter(
          (special): special is NonNullable<typeof special> =>
            Boolean(special) &&
            specialMatchesActiveMenuType(special.menuType, menuType) &&
            isSpecialKindEligible(special.kind)
        )
        .map((special) => ({
          id: special.id,
          name: special.name,
          ...(special.description ? { description: special.description } : {}),
          ...(special.startDate ? { startDate: special.startDate } : {}),
          ...(special.endDate ? { endDate: special.endDate } : {}),
          kind: mapSpecialKind(special.kind),
        }));

  const { getPriceOptions } = useProductPricing();
  const priceOptions = getPriceOptions(product);

  const collectionCardBadge = product.collectionCardBadge ?? null;

  return {
    href,
    isStaffPick,
    isSponsored,
    brandName,
    strainType,
    potency,
    isSpecialOffer,
    specialNames,
    specials,
    hideDiscount,
    hideSalesLanguage,
    priceOptions,
    collectionCardBadge,
  };
};
